'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ExternalLink, Edit, Trash2, Clock, User, Link as LinkIcon, Bookmark, Check, X, Sparkle, Send, RotateCcw, Copy, RefreshCcw, ArrowLeft, Flag } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { fetchNewsClippingById, deleteNewsClipping, type NewsClipping, updateNewsClipping, fetchNewsClippings, isClippingSaved, unsaveClipping, saveClipping, createNewsClipping, createClipReport } from '@/utils/supabase-queries'
import { getProxiedImageUrl } from '@/utils/image-proxy'
import { Loading } from '@/components/ui/loading'
import QuotesSection from '@/components/media/QuotesSection'
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog"
import { toast } from "react-hot-toast"
import { BrandLogo } from './ClipCardView'
import { getTagColors } from '@/components/features/ActivityGraph/utils/styleUtils'
import { getLanguageCode, commonLanguages } from '@/utils/languageUtils'
import { LanguageSwitcher } from '@/components/ui/language-switcher'

// Helper function to format reach with thousands separator
const formatReach = (reach: number | null | undefined): string => {
  if (!reach || reach === 0) return '0';
  return reach.toLocaleString('en-US');
};

// Format date in DD-MM-YYYY format
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    return '';
  }
};

// Separate component for news clipping image
function NewsClippingImage({ 
  imageUrl, 
  title, 
  date,
  url,
  brandName 
}: { 
  imageUrl?: string;
  title?: string;
  date?: string;
  url?: string;
  brandName?: string;
}) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (imageUrl) {
      setImgSrc(getProxiedImageUrl(imageUrl));
      setIsLoading(true);
      setHasError(false);
    } else {
      setImgSrc(null);
      setIsLoading(false);
    }
  }, [imageUrl]);

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    setImgSrc('/images/news-placeholder.jpg');
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  return (
    <div className="relative w-full h-full">
      {imgSrc ? (
        <div className="w-full aspect-video relative h-full">
          {isLoading && !hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-pulse text-gray-400">Loading image...</div>
            </div>
          )}
          <img
            src={imgSrc}
            alt={title || 'Media clip'}
            className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 object-top`}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        </div>
      ) : (
        <div className="w-full aspect-video bg-gradient-to-r from-gray-800 to-gray-700 flex items-center justify-center h-full">
          <span className="text-gray-400">No image available</span>
        </div>
      )}
    </div>
  );
}

interface ClipDetailsProps {
  clipId?: string; // Made optional to support create mode
  onDeleteSuccess?: () => void;
  onClose?: () => void;
  isSavedInitial?: boolean; // Made optional since it's not relevant in create mode
  onToggleSave?: (clipId: string) => Promise<void>;
  canDelete?: boolean;
  onCreateSuccess?: () => void; // Added new callback for creation success
}

export default function ClipDetails({ 
  clipId, 
  onDeleteSuccess, 
  onClose, 
  isSavedInitial = false, // Default value
  onToggleSave, 
  canDelete = false, // Default value
  onCreateSuccess
}: ClipDetailsProps) {
  const { user, loading: authLoading, canPerformAction } = useAuth()
  const [newsClipping, setNewsClipping] = useState<NewsClipping | null>(null)
  const isCreateMode = !clipId;
  const [isLoading, setIsLoading] = useState(isCreateMode ? false : true) // Only load if viewing existing
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [isEditing, setIsEditing] = useState(isCreateMode) // Start in edit mode if creating
  const [editedClipping, setEditedClipping] = useState<Partial<NewsClipping>>(
    isCreateMode ? {
      title: '',
      summary: '',
      summary_original: '',
      url: '',
      source: '',
      category: '',
      sentiment: '',
      visibility: '',
      date: new Date().toISOString().split('T')[0], // Default to today
      image_url: '',
      quote_1: '',
      quote_2: '',
      quote_3: '',
      brandName: '',
      Language: 'English'
    } : {}
  )
  const [isSaving, setIsSaving] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({
    category: [],
    sentiment: [],
    source: [],
    brandName: [],
    visibility: [],
    Language: []
  })
  const [isSaveLoading, setIsSaveLoading] = useState(false)
  const [error, setError] = useState<string | null>(null);
  const [isReevaluating, setIsReevaluating] = useState(false);
  
  // State for language selection modal
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [languageModalAction, setLanguageModalAction] = useState<'summary' | 'post' | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('English');

  // State for report modal
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);

  // Function to open the language modal
  const openLanguageModal = (action: 'summary' | 'post') => {
    setSelectedLanguage('English'); // Default to English
    setLanguageModalAction(action);
    setIsLanguageModalOpen(true);
  };

  // Function to handle confirming language and sending message
  const handleConfirmLanguageAndSend = () => {
    if (!newsClipping || !clipId || !languageModalAction) return;

    let baseMessage = '';
    if (languageModalAction === 'summary') {
      baseMessage = `Please create an article summary as a PR Specialist for clip ID: ${clipId}`; // Original summary message
    } else if (languageModalAction === 'post') {
      baseMessage = `Create a social media post based on the news clip with ID: ${clipId}. Title: ${newsClipping.title || 'Untitled'}`; // Original post message
    }

    const finalMessage = `${baseMessage}. Output should be in ${selectedLanguage}.`;

    console.log('[ClipDetails] Dispatching message with language:', finalMessage);
    window.dispatchEvent(new CustomEvent('open-chat-with-message', {
      detail: { message: finalMessage }
    }));

    setIsLanguageModalOpen(false); // Close modal
    setLanguageModalAction(null);
    onClose?.(); // Optionally close the main clip details panel
  };
  
  // Function to trigger AI summary for the current clip - LANGUAGE DEPENDENT
  const handleClipAISummary = () => {
    if (!newsClipping || !clipId) return;
    
    const clipLanguage = displayData?.Language;
    
    // If clip is in English, proceed directly with English
    if (clipLanguage === 'English') {
      const baseMessage = `Please create an article summary as a PR Specialist for clip ID: ${clipId}`;
      const finalMessage = `${baseMessage}. Output should be in English.`;
      
      console.log('[ClipDetails] Dispatching message directly in English:', finalMessage);
      window.dispatchEvent(new CustomEvent('open-chat-with-message', {
        detail: { message: finalMessage }
      }));
      onClose?.();
    } else {
      // If clip is in another language, show modal with English + that language
      openLanguageModal('summary');
    }
  };
  
  // Function to trigger Post creation - LANGUAGE DEPENDENT
  const handleCreatePost = () => {
    if (!newsClipping || !clipId) {
      if (!newsClipping || !clipId) {
          toast.error("Clip data is not available.");
      }
      return;
    }
    
    const clipLanguage = displayData?.Language;
    
    // If clip is in English, proceed directly with English
    if (clipLanguage === 'English') {
      const baseMessage = `Create a social media post based on the news clip with ID: ${clipId}. Title: ${newsClipping.title || 'Untitled'}`;
      const finalMessage = `${baseMessage}. Output should be in English.`;
      
      console.log('[ClipDetails] Dispatching message directly in English:', finalMessage);
      window.dispatchEvent(new CustomEvent('open-chat-with-message', {
        detail: { message: finalMessage }
      }));
      onClose?.();
    } else {
      // If clip is in another language, show modal with English + that language
      openLanguageModal('post');
    }
  };

  // Load available options
  useEffect(() => {
    async function loadOptions() {
      try {
        const allClippings = await fetchNewsClippings()
        const categories = new Set<string>()
        const sentiments = new Set<string>()
        const sources = new Set<string>()
        const brands = new Set<string>()
        const visibilities = new Set<string>()
        
        allClippings.forEach(clip => {
          if (clip.category) categories.add(clip.category)
          if (clip.sentiment) sentiments.add(clip.sentiment)
          if (clip.source) sources.add(clip.source)
          if (clip.brandName) brands.add(clip.brandName)
          if (clip.visibility) visibilities.add(clip.visibility)
        })
        
        setFilterOptions({
          category: Array.from(categories).sort(),
          sentiment: Array.from(sentiments).sort(),
          source: Array.from(sources).sort(),
          brandName: Array.from(brands).sort(),
          visibility: Array.from(visibilities).sort(),
          Language: []
        })
      } catch (err) {
        console.error('Failed to load filter options:', err)
        toast.error("Failed to load dropdown options.");
      }
    }
    loadOptions()
  }, [])
  
  // Load news clipping data when clipId changes or component mounts
  useEffect(() => {
    const loadNewsClipping = async () => {
      if (!clipId || isCreateMode) {
        setIsLoading(false);
        setNewsClipping(null); // Ensure it's null in create mode initially
        setEditedClipping(isCreateMode ? {
          title: '',
          summary: '',
          summary_original: '',
          url: '',
          source: '',
          category: '',
          sentiment: '',
          visibility: '',
          date: new Date().toISOString().split('T')[0],
          image_url: '',
          quote_1: '',
          quote_2: '',
          quote_3: '',
          brandName: '',
          Language: 'English'
        } : {});
        setIsEditing(true); // Start in edit mode for new clip
        return;
      }
      
      setIsLoading(true);
      setError(null); // Reset error state
      try {
        const clipping = await fetchNewsClippingById(clipId);
        if (clipping) {
          setNewsClipping(clipping);
          setEditedClipping(clipping); // Initialize edit form with fetched data
          setIsEditing(false); // Start in view mode for existing clip
          if (user?.id) {
            const saved = await isClippingSaved(clipId, user.id);
            setIsSaved(saved);
          }
        } else {
          setError("News clipping not found.");
          setNewsClipping(null);
        }
      } catch (err: any) {
        console.error("Error fetching news clipping:", err);
        setError(err.message || "An unexpected error occurred.");
        setNewsClipping(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isCreateMode) {
        loadNewsClipping();
    }
  }, [clipId, user?.id]);
  
  // Handle local save state, call parent function on change
  const handleToggleSave = async () => {
    if (!user || !clipId || isSaveLoading || !onToggleSave) return;
    setIsSaveLoading(true);
    try {
      await onToggleSave(clipId);
      setIsSaved(!isSaved);
      toast.success(isSaved ? 'Clipping unsaved!' : 'Clipping saved!');
    } catch (err) {
      console.error('Error toggling save status:', err);
      toast.error('Failed to update save status.');
    } finally {
      setIsSaveLoading(false);
    }
  };
  
  // Use isSavedInitial from props
  useEffect(() => {
    if (!isCreateMode) {
      setIsSaved(isSavedInitial);
    }
  }, [isSavedInitial, isCreateMode]);

  const handleDelete = async () => {
    if (!newsClipping || !canDelete) {
      toast.error('You do not have permission to delete media clips.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this news clipping? This action cannot be undone.')) {
      setIsDeleting(true)
      try {
        await deleteNewsClipping(newsClipping.id)
        toast.success('News clipping deleted successfully!')
        onDeleteSuccess?.();
        onClose?.();
      } catch (err: any) {
        console.error('Error deleting news clipping:', err)
        toast.error(`Failed to delete: ${err.message || 'Unknown error'}`);
      } finally {
        setIsDeleting(false)
      }
    }
  }
  
  const handleEditToggle = () => {
      if (isCreateMode) return; // Can't toggle if creating

      if (!canPerformAction('NewsClipping', 'update')) {
          toast.error('You do not have permission to edit media clips.');
          return;
      }
      
      setIsEditing(!isEditing)
      
      // Reset edited state if cancelling edit
      if (isEditing && newsClipping) {
          setEditedClipping({
            ...newsClipping,
            image_url: newsClipping.image_url || ''
          })
      }
  }
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setEditedClipping(prev => ({ ...prev, [name]: value }))
  }

  const handleSelectChange = (name: string) => (value: string) => {
    setEditedClipping(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
      if (!editedClipping) {
          toast.error('Cannot save changes.');
          return;
      }
      
      setIsSaving(true)
      
      try {
          if (isCreateMode) {
              // CREATE MODE
              if (!canPerformAction('NewsClipping', 'insert')) {
                  toast.error('You do not have permission to create media clips.');
                  return;
              }

              // Validate required fields
              if (!editedClipping.title) {
                  toast.error('Title is required.');
                  return;
              }
              
              if (!editedClipping.url) {
                  toast.error('URL is required.');
                  return;
              }
              
              if (!editedClipping.category) {
                  toast.error('Category is required.');
                  return;
              }

              // Create new clipping
              const newClip = await createNewsClipping(editedClipping);
              toast.success('Media clip created successfully!');
              onCreateSuccess?.();
              onClose?.();
          } else {
              // UPDATE MODE
              if (!newsClipping || !canPerformAction('NewsClipping', 'update')) {
                  toast.error('Cannot save changes.');
                  return;
              }

              const result = await updateNewsClipping(newsClipping.id, editedClipping);
              setNewsClipping(result);
              setEditedClipping({
                ...result,
                image_url: result.image_url || ''
              });
              setIsEditing(false);
              toast.success('News clipping updated successfully!');
          }
      } catch (err: any) {
          console.error(`Error ${isCreateMode ? 'creating' : 'updating'} news clipping:`, err);
          toast.error(`Failed to ${isCreateMode ? 'create' : 'update'}: ${err.message || 'Unknown error'}`);
      } finally {
          setIsSaving(false);
      }
  }

  const handleReevaluate = async () => {
    if (!clipId || isReevaluating || !displayData?.url) {
      if (!displayData?.url) {
        toast.error('Cannot reevaluate: Clip URL is missing.');
      }
      return;
    }

    setIsReevaluating(true);
    const webhookUrl = 'https://n8n.automize.site/webhook/c930423b-d083-4218-835d-4ae1e7711ab5'; // Removed clipId from URL
    
    const payload = {
      id: parseInt(clipId, 10), // Send id as number, matching example
      URL: displayData.url,      // Send URL field
      "Brand name": displayData.brandName || null, // Send Brand name field, defaulting to null if missing
      "local_brand_name": displayData.local_brand_name || null // Added local brand name
    };

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST', // Changed to POST
        headers: {
          'Content-Type': 'application/json', // Added Content-Type header
        },
        body: JSON.stringify(payload), // Added JSON body
      });
      
      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || JSON.stringify(errorData);
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(errorMsg);
      }
      
      toast.success('Reevaluation request sent successfully!');

    } catch (error: any) {
      console.error('Error sending reevaluation request:', error);
      toast.error(`Failed to send reevaluation request: ${error.message || 'Unknown error'}`);
    } finally {
      setIsReevaluating(false);
    }
  };

  // Function to handle sending the report
  const handleSendReport = async () => {
    if (!reportMessage.trim()) {
      toast.error("Please enter a message for the report.");
      return;
    }
    if (!displayData || !displayData.id || !user?.id) {
      toast.error("Clip data or user information is missing.");
      return;
    }

    setIsSendingReport(true);
    // const webhookUrl = 'https://n8n.automize.site/webhook-test/98cf9eab-4e4d-4220-8625-d778a15d84e1'; // Removed webhook URL

    // const payload = { // Removed webhook payload
    //   clipDetails: displayData,
    //   userMessage: reportMessage,
    //   userId: user?.id,
    //   userEmail: user?.email
    // };

    try {
      // Removed fetch call to webhook
      // const response = await fetch(webhookUrl, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(payload),
      // });

      // if (!response.ok) {
      //   let errorMsg = `HTTP error! status: ${response.status}`;
      //   try {
      //     const errorData = await response.json();
      //     errorMsg = errorData.message || JSON.stringify(errorData);
      //   } catch (e) {
      //     // Ignore if response body is not JSON or empty
      //   }
      //   throw new Error(errorMsg);
      // }

      // Call the new function to create the report in the database
      await createClipReport({
        clip_id: displayData.id,
        user_id: user.id,
        message: reportMessage,
      });

      toast.success('Report sent successfully!');
      setIsReportModalOpen(false);
      setReportMessage(''); // Clear message after sending
    } catch (error: any) {
      console.error('Error sending report:', error);
      toast.error(`Failed to send report: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSendingReport(false);
    }
  };

  // Render loading state
  if (isLoading || authLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-4">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-4">
        <p className="text-red-500">{error}</p>
        {onClose && <Button onClick={onClose} className="mt-4">Close</Button>}
      </div>
    );
  }

  if (!isCreateMode && !newsClipping && !isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-4">
        <p className="text-gray-500">Media clip not found or could not be loaded.</p>
        {onClose && <Button onClick={onClose} className="mt-4">Close</Button>}
      </div>
    );
  }
  
  // Use edited data when editing, otherwise use fetched data
  const displayData = isEditing ? editedClipping : newsClipping;
  
  // Calculate tag colors
  const sentimentColors = displayData?.sentiment 
    ? getTagColors('sentiment', displayData.sentiment) 
    : { backgroundColor: '#E5E7EB', textColor: '#374151' };
    
  const categoryColors = displayData?.category 
    ? getTagColors('category', displayData.category) 
    : { backgroundColor: '#E5E7EB', textColor: '#374151' };

  // ADDED Language display value
  const languageDisplay = displayData?.Language ? getLanguageCode(displayData.Language) : null;

  return (
    <div className="flex flex-col h-full bg-white overflow-x-hidden">
      {/* Title - REMOVED */}
      {/* <h2 className="text-lg font-medium p-4 border-b bg-gray-50 flex-shrink-0">
        {isCreateMode ? 'Create New Media Clip' : (isEditing ? 'Edit Media Clip' : displayData?.title)}
      </h2> */}
      
      {/* Sticky Top Action Bar */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm p-3 border-b border-gray-200 flex justify-end items-center space-x-2 h-[60px]">
        {/* Close/Back Button - Moved to the left */}
        {onClose && !isEditing && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            title="Back"
            className="mr-auto hover:bg-gray-100 rounded-md"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}

        {/* Original Link Button */}
        {!isCreateMode && displayData?.url && (
          <Button 
            variant="outline" 
            size="icon"
            asChild 
            className="border rounded-md hover:bg-gray-100"
            title="Open Original Article"
          >
            <a href={displayData.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        )}

        {/* Save/Unsave Button - Only show for existing clips */}
        {!isCreateMode && onToggleSave && (
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleToggleSave} 
            disabled={isSaveLoading}
            title={isSaved ? "Unsave Clipping" : "Save Clipping"}
            className={`border rounded-md hover:bg-gray-100 ${isSaved ? 'bg-purple-100 border-purple-300 text-purple-700 hover:bg-purple-200' : 'bg-white'}`}
          >
            {isSaveLoading ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Bookmark className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />}
          </Button>
        )}

        {/* Edit/Save/Cancel Buttons */}
        {isEditing ? (
          <>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={isCreateMode ? onClose : handleEditToggle} 
              disabled={isSaving} 
              title="Cancel"
              className="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 rounded-md"
            >
              <X className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleSaveEdit} 
              disabled={isSaving} 
              title={isCreateMode ? "Create Clip" : "Save Changes"}
              className="bg-green-100 border-green-300 text-green-700 hover:bg-green-200 rounded-md"
            >
              {isSaving ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
          </>
        ) : (
          canPerformAction('NewsClipping', 'update') && (
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleEditToggle} 
              title="Edit Clipping"
              className="border rounded-md hover:bg-gray-100"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )
        )}
        
        {/* Delete Button - Only show for existing clips */}
        {!isCreateMode && canDelete && (
          <Button 
            variant="destructive" 
            size="icon" 
            onClick={handleDelete} 
            disabled={isDeleting || isEditing}
            title="Delete Clipping"
            className="bg-red-500 hover:bg-red-600 text-white rounded-md"
          >
            {isDeleting ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        )}

        {/* Copy URL Button - Moved to the right */}
        {!isCreateMode && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set('clipId', clipId!);
              const urlToCopy = currentUrl.toString();

              navigator.clipboard.writeText(urlToCopy)
                .then(() => toast.success('Clip URL copied to clipboard!'))
                .catch(err => {
                   console.error('Failed to copy URL: ', err);
                   toast.error('Failed to copy URL.');
                });
            }}
            title="Copy Clip URL"
            className="border rounded-md hover:bg-gray-100"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden"> 
        {/* Image */}
        <div className="relative w-full aspect-video">
          <NewsClippingImage 
            imageUrl={displayData?.image_url}
            title={displayData?.title}
            date={displayData?.date}
            url={displayData?.url}
            brandName={displayData?.brandName}
          />
        </div>
        
        {/* Content Padding */}
        <div className="p-4 space-y-4">
          {/* Title */}
          {isEditing ? (
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
              <Input 
                id="title"
                name="title" 
                value={editedClipping.title || ''} 
                onChange={handleInputChange} 
                className="text-xl font-semibold"
                required
              />
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{displayData?.title || 'No Title'}</h1>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            {/* URL - Only in edit mode */}
            {isEditing && (
              <div className="w-full mb-2">
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">URL*</label>
                <Input 
                  id="url"
                  name="url" 
                  value={editedClipping.url || ''} 
                  onChange={handleInputChange}
                  placeholder="https://..."
                  required 
                />
              </div>
            )}
            
            {/* Source */}
            <div className="flex items-center gap-1">
              <LinkIcon className="h-4 w-4 text-gray-400" />
              {isEditing ? (
                 <Select 
                   name="source"
                   value={editedClipping.source || ''} 
                   onValueChange={handleSelectChange('source')}
                 >
                   <SelectTrigger className="w-auto h-8 text-xs p-1">
                     <SelectValue placeholder="Select Source" />
                   </SelectTrigger>
                   <SelectContent>
                     {filterOptions.source.map(option => (
                       <SelectItem key={option} value={option}>{option}</SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
               ) : displayData?.source ? (
                 <a href={displayData.url || '#'} target="_blank" rel="noopener noreferrer" className="hover:underline">{displayData.source}</a>
               ) : (
                 <span className="text-gray-500">No source</span>
               )}
            </div>
            
            {/* Date */}
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-gray-400" />
              {isEditing ? (
                <Input 
                  type="date" 
                  name="date" 
                  value={editedClipping.date ? editedClipping.date.split('T')[0] : ''} 
                  onChange={handleInputChange} 
                  className="h-8 text-xs p-1"
                />
              ) : (
                <span>{formatDate(displayData?.date)}</span>
              )}
            </div>
          </div>
          
          {/* Brand & Tags */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Brand Tag */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5">
              {displayData?.brandName && <BrandLogo brandName={displayData.brandName} />}
              {isEditing ? (
                 <Select 
                   name="brandName"
                   value={editedClipping.brandName || ''} 
                   onValueChange={handleSelectChange('brandName')}
                 >
                   <SelectTrigger className="w-auto h-6 text-xs p-1 border-none bg-transparent focus:ring-0">
                     <SelectValue placeholder="Select Brand" />
                   </SelectTrigger>
                   <SelectContent>
                     {filterOptions.brandName.map(option => (
                       <SelectItem key={option} value={option}>{option}</SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
               ) : (
                 <span className="text-xs font-medium text-gray-700">{displayData?.brandName || 'No brand'}</span>
               )}
            </div>
            
            {/* Sentiment Tag */}
            {isEditing ? (
               <Select 
                 name="sentiment"
                 value={editedClipping.sentiment || ''} 
                 onValueChange={handleSelectChange('sentiment')}
               >
                 <SelectTrigger 
                   className="w-auto h-6 text-xs px-2 py-0.5 rounded-full border-none focus:ring-0 font-medium" 
                   style={{ backgroundColor: sentimentColors.backgroundColor, color: sentimentColors.textColor }}
                 >
                   <SelectValue placeholder="Select Sentiment" />
                 </SelectTrigger>
                 <SelectContent>
                   {filterOptions.sentiment.map(option => {
                     const { backgroundColor, textColor } = getTagColors('sentiment', option);
                     return (
                       <SelectItem key={option} value={option} style={{ backgroundColor, color: textColor }}>
                         {option}
                       </SelectItem>
                     );
                   })}
                 </SelectContent>
               </Select>
             ) : displayData?.sentiment ? (
               <span 
                 className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                 style={{ backgroundColor: sentimentColors.backgroundColor, color: sentimentColors.textColor }}
               >
                 {displayData.sentiment}
               </span>
             ) : null}
            
            {/* Category Tag */}
            {isEditing ? (
               <Select 
                 name="category"
                 value={editedClipping.category || ''} 
                 onValueChange={handleSelectChange('category')}
               >
                 <SelectTrigger 
                   className="w-auto h-6 text-xs px-2 py-0.5 rounded-full border-none focus:ring-0 font-medium" 
                   style={{ backgroundColor: categoryColors.backgroundColor, color: categoryColors.textColor }}
                 >
                   <SelectValue placeholder={isCreateMode ? "Select Category *" : "Select Category"} />
                 </SelectTrigger>
                 <SelectContent>
                   {filterOptions.category.map(option => {
                     const { backgroundColor, textColor } = getTagColors('category', option);
                     return (
                       <SelectItem key={option} value={option} style={{ backgroundColor, color: textColor }}>
                         {option}
                       </SelectItem>
                     );
                   })}
                 </SelectContent>
               </Select>
             ) : displayData?.category ? (
               <span 
                 className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                 style={{ backgroundColor: categoryColors.backgroundColor, color: categoryColors.textColor }}
               >
                 {displayData.category}
               </span>
             ) : null}

            {/* Language Tag - Displayed in view mode, after category */} 
            {!isEditing && languageDisplay && (
              <span 
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 ml-1"
                title={displayData?.Language} // Show full language name on hover
              >
                {languageDisplay.toLowerCase()}
              </span>
            )}
            
            {/* Language Select - Displayed in edit mode, after category select */} 
            {isEditing && (
              <Select
                name="Language" // Field name in the DB
                value={editedClipping.Language || ''} // Storing full name like "English"
                onValueChange={handleSelectChange('Language')}
              >
                <SelectTrigger
                  className="w-auto h-6 text-xs px-2 py-0.5 rounded-full border-none focus:ring-0 font-medium bg-gray-100 text-gray-700 ml-1"
                >
                  <SelectValue placeholder="Select Language" />
                </SelectTrigger>
                <SelectContent>
                  {commonLanguages.map(lang => (
                    <SelectItem key={lang.code} value={lang.name} className="text-xs">
                      {lang.name} {/* e.g., English, Greek */}
                    </SelectItem>
                  ))}
                  {/* Optionally add an option for "Other" or if not in list */}
                  {!commonLanguages.some(lang => lang.name === editedClipping.Language) && editedClipping.Language && (
                    <SelectItem key={editedClipping.Language} value={editedClipping.Language} className="text-xs">
                      {editedClipping.Language} (Custom)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}

            {/* Visibility Tag */}
            {isEditing ? (
              <Select
                name="visibility"
                value={editedClipping.visibility || ''}
                onValueChange={handleSelectChange('visibility')}
              >
                <SelectTrigger
                  className="w-auto h-6 text-xs px-2 py-0.5 rounded-full border-none focus:ring-0 font-medium bg-gray-200 text-gray-700"
                >
                  <SelectValue placeholder="Select Visibility" />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.visibility.map(option => (
                    <SelectItem key={option} value={option} className="bg-gray-200 text-gray-700">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : displayData?.visibility ? (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700"
              >
                {displayData.visibility}
              </span>
            ) : null}

            {/* Reach Tag */}
            {!isEditing && displayData?.reach && displayData.reach > 0 && (
              <span 
                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                title={`Estimated reach: ${formatReach(displayData.reach)}`}
              >
                Reach: {formatReach(displayData.reach)}
              </span>
            )}
          </div>
          
          {/* Add Separator Here */}
          <hr className="my-4 border-gray-200" />

          {/* Reach Breakdown Section (New) */}
          {/* Reach components are removed as per the new code block */}

          {/* Summary */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-800">Summary</h2>
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label htmlFor="summary" className="block text-xs font-medium text-gray-600 mb-1">English Summary</label>
                  <Textarea 
                    id="summary"
                    name="summary" 
                    value={editedClipping.summary || ''}
                    onChange={handleInputChange} 
                    rows={4} 
                    className="text-sm text-gray-700"
                  />
                </div>
                <div>
                  <label htmlFor="summary_original" className="block text-xs font-medium text-gray-600 mb-1">Original Summary</label>
                  <Textarea 
                    id="summary_original"
                    name="summary_original" 
                    value={editedClipping.summary_original || ''}
                    onChange={handleInputChange} 
                    rows={4} 
                    className="text-sm text-gray-700"
                  />
                </div>
              </div>
            ) : (
              <LanguageSwitcher
                originalText={displayData?.summary_original}
                englishText={displayData?.summary}
              />
            )}
          </div>
          
          {/* Add Separator Here */}
          <hr className="my-4 border-gray-200" />

          {/* Quotes */}
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-800">Quotes</h2>
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label htmlFor="quote_1" className="block text-xs font-medium text-gray-600 mb-1">Quote 1</label>
                  <Textarea 
                    id="quote_1"
                    name="quote_1" 
                    value={editedClipping.quote_1 || ''}
                    onChange={handleInputChange} 
                    rows={2} 
                    className="text-sm text-gray-700"
                  />
                </div>
                <div>
                  <label htmlFor="quote_2" className="block text-xs font-medium text-gray-600 mb-1">Quote 2</label>
                  <Textarea 
                    id="quote_2"
                    name="quote_2" 
                    value={editedClipping.quote_2 || ''} 
                    onChange={handleInputChange} 
                    rows={2} 
                    className="text-sm text-gray-700"
                  />
                </div>
                <div>
                  <label htmlFor="quote_3" className="block text-xs font-medium text-gray-600 mb-1">Quote 3</label>
                  <Textarea 
                    id="quote_3"
                    name="quote_3" 
                    value={editedClipping.quote_3 || ''} 
                    onChange={handleInputChange} 
                    rows={2} 
                    className="text-sm text-gray-700"
                  />
                </div>
              </div>
             ) : (
               <QuotesSection 
                 quote_1={displayData?.quote_1} 
                 quote_2={displayData?.quote_2} 
                 quote_3={displayData?.quote_3} 
                 brandName={displayData?.brandName} 
                 localBrandName={displayData?.local_brand_name}
                 allBrandNames={filterOptions.brandName}
               />
             )}
          </div>
          
          {/* Image URL Edit */}
          {isEditing && (
            <div className="space-y-2">
              <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700">Image URL</label>
              <Input 
                id="imageUrl"
                name="image_url" 
                value={editedClipping.image_url || ''} 
                onChange={handleInputChange} 
                className="text-sm text-gray-700"
                placeholder="Enter image URL"
              />
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="p-3 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2 bg-gray-50">
        {/* Group AI/Reevaluate/Post buttons - Moved to the right */}
        <div className="flex items-center space-x-2 ml-auto">
          {/* Report Button */}
          {!isCreateMode && !isEditing && (
            <Button
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-2 text-sm font-medium rounded-md hover:opacity-90 flex items-center"
              onClick={() => setIsReportModalOpen(true)}
              title="Report Clip Issue"
              disabled={isSendingReport || isReevaluating}
            >
              {isSendingReport ? 
                <RotateCcw className="h-4 w-4 animate-spin mr-0 sm:mr-2" /> : 
                <Flag className="h-4 w-4 mr-0 sm:mr-2" />
              }
              <span className="hidden sm:inline">Report</span>
            </Button>
          )}
          
          {/* Reevaluate Button */}
          {!isCreateMode && !isEditing && (
            <Button
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-2 text-sm font-medium rounded-md hover:opacity-90 flex items-center"
              onClick={handleReevaluate}
              disabled={isReevaluating}
              title="Reevaluate Clip"
            >
              {isReevaluating ? 
                <RotateCcw className="h-4 w-4 animate-spin mr-0 sm:mr-2" /> : 
                <RefreshCcw className="h-4 w-4 mr-0 sm:mr-2" />
              } 
              <span className="hidden sm:inline">Reevaluate</span>
            </Button>
          )}

          {/* AI Summary Button - MODIFIED onClick */}
          {!isCreateMode && !isEditing && (
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-2 text-sm font-medium rounded-md hover:opacity-90 flex items-center"
              onClick={handleClipAISummary}
            >
              <Sparkle className="h-4 w-4 mr-2" /> 
              <span className="">Summary</span>
            </Button>
          )}
          
          {/* Create Post Button - MODIFIED onClick */}
          {!isCreateMode && !isEditing && (
            <Button
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-2 text-sm font-medium rounded-md hover:opacity-90 flex items-center"
              onClick={handleCreatePost}
            >
              <Send className="h-4 w-4 mr-2" /> 
              <span className="">Post</span>
            </Button>
          )}
        </div>
      </div>

      {/* Language Selection Modal */}
      <Dialog open={isLanguageModalOpen} onOpenChange={setIsLanguageModalOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white">
          <DialogHeader>
            <DialogTitle>Select Output Language</DialogTitle>
            <DialogDescription>
              Choose the language for the AI generated {languageModalAction}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Select value={selectedLanguage} onValueChange={(value: string) => setSelectedLanguage(value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                {/* Show the clip's language as second option if it's not English */}
                {displayData?.Language && displayData.Language !== 'English' && (
                  <SelectItem value={displayData.Language}>{displayData.Language}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLanguageModalOpen(false)}>Cancel</Button>
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
              onClick={handleConfirmLanguageAndSend}
            >
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={isReportModalOpen} onOpenChange={setIsReportModalOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white">
          <DialogHeader>
            <DialogTitle>Report Clip Issue</DialogTitle>
            <DialogDescription>
              Please provide details about the issue you're reporting.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Textarea
              id="reportMessage"
              name="reportMessage"
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
              placeholder="Enter your report details here..."
              rows={4}
              className="text-sm text-gray-700"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReportModalOpen(false)} disabled={isSendingReport}>Cancel</Button>
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 flex items-center justify-center px-3 py-2"
              onClick={handleSendReport}
              disabled={isSendingReport || !reportMessage.trim()}
            >
              {isSendingReport ? 
                <><RotateCcw className="h-4 w-4 animate-spin mr-2" /> Submitting...</> : 
                'Submit Report'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 